"use client";
import type {
  AuditFormProps,
  SessionProgress,
  SetSessionProgress,
} from "../../../types/audit-form";

import type { AuditFileMetadata } from "../../../types/audit-file";
import { useLatest } from "../../../hooks/use-latest";
import { useImageDescriptions } from "../../../hooks/use-image-descriptions";
import {
  errorMessage,
  requireMutationSuccess,
} from "../../../utils/mutationResult.js";

import { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Badge } from "../../ui/badge";
import { Button } from "../../ui/button.jsx";
import { Card, CardContent, CardHeader, CardTitle } from "../../ui/card.jsx";

import {
  AlertCircle,
  Camera,
  ChevronDown,
  ChevronUp,
  Eye,
  Loader2,
  Save,
  Trash2,
  X,
} from "lucide-react";

import { useDebouncedSave } from "../../../hooks/use-debounced-save";
import { useDraftAutosave } from "../../../hooks/use-draft-autosave";
import { useStableMemoComponent } from "../../../hooks/use-stable-memo-component";
import {
  useVersionAwareFiles,
  useVersionAwareSiteData,
} from "../../../hooks/use-version-aware-data";
import { useNotification } from "../../../hooks/useNotification";
import { useAppDispatch, useAppSelector } from "../../../redux/hooks";
import {
  deleteFile,
  fetchFiles,
  updateFile,
  uploadMultipleFiles,
} from "../../../redux/slices/fileSlice.js";
import { updateSiteData } from "../../../redux/slices/siteDataSlice.js";
import { sharedOperationalAspectsProgress } from "../../../utils/componentUtils.js";
import { notificationHelpers } from "../../../utils/notificationHelpers";
import { OfflineImage } from "../../shared/offline-image";
import { LoadingIndicator } from "../../ui/loading-indicator";
import {
  OptimizedCheckbox,
  OptimizedInput,
  OptimizedTextarea,
} from "../optimized-form-field";
import { ImageCapturePicker } from "./image-capture-picker";
import { LocalSectionImportControl } from "./local-section-import-control";

interface OperationalAspectsFormProps extends AuditFormProps {
  siteId: string;
  templateId: string;
  reportId: string;
  readOnly?: boolean;
  isViewingHistoricalVersion?: boolean;
  versionData?: any;
  sessionProgress: SessionProgress;
  setSessionProgress: SetSessionProgress;
  previousCompletedReports?: any[];
}

interface OperationalAspectsImage extends AuditFileMetadata {
  id: string;
  name: string;
  url: string;
  type: string;
  size: number;
  description: string;
  uploadDate: Date;
}

interface OperationalAspectsData {
  // Civil Section
  civilSiteConditions: string;
  civilSiteAccessRoadCondition: string;
  civilLVCablesManholesCondition: string;
  civilHVCablesManholesCondition: string;
  civilFoundationCondition: string;
  civilTransformerCondition: string;

  // Mechanical Section - Framework
  mechanicalFrameworkAppropriateRammingHead: boolean;
  mechanicalFrameworkAppropriateRammingHeadComment: string;
  mechanicalFrameworkAlignmentTwisted: boolean;
  mechanicalFrameworkAlignmentTwistedComment: string;
  mechanicalFrameworkRammingDepth: string;
  mechanicalFrameworkUndergroundSurprises: boolean;
  mechanicalFrameworkUndergroundSurprisesComment: string;
  mechanicalFrameworkRegalvanizationMethod: string;
  mechanicalFrameworkDrillingConcrete: boolean;
  mechanicalFrameworkDrillingConcreteComment: string;
  mechanicalFrameworkAverageRemainingTime: string;
  mechanicalFrameworkSignsOfCorrosion: boolean;
  mechanicalFrameworkSignsOfCorrosionComment: string;

  // Mechanical Section - Purlin & Attachments
  mechanicalPurlinSignsOfCorrosion: boolean;
  mechanicalPurlinSignsOfCorrosionComment: string;
  mechanicalPurlinVisibleDeformation: boolean;
  mechanicalPurlinVisibleDeformationComment: string;

  // Mechanical Section - Module Installation
  mechanicalModuleSynchronizedAlignment: boolean;
  mechanicalModuleSynchronizedAlignmentComment: string;
  mechanicalModuleClampInstallationCheck: boolean;
  mechanicalModuleClampInstallationCheckComment: string;

  // Electrical Section - String Cable Management (Above Ground)
  electricalStringAboveLooseCableConnector: boolean;
  electricalStringAboveLooseCableConnectorComment: string;
  electricalStringAboveProperSupport: boolean;
  electricalStringAboveProperSupportComment: string;
  electricalStringAboveNoSharpEdge: boolean;
  electricalStringAboveNoSharpEdgeComment: string;
  electricalStringAboveCableNumbering: boolean;
  electricalStringAboveCableNumberingComment: string;

  // Electrical Section - String Cable Management (Below Ground)
  electricalStringBelowRoutedThroughConduit: boolean;
  electricalStringBelowRoutedThroughConduitComment: string;
  electricalStringBelowConduitSealedWithFoam: boolean;
  electricalStringBelowConduitSealedWithFoamComment: string;

  // Electrical Section - DC Main Cable Management
  electricalDCMainCablesWithoutEndCap: boolean;
  electricalDCMainCablesWithoutEndCapComment: string;
  electricalDCMainCableSpecification: string;
  electricalDCMainInstallationMethodology: string;
  electricalDCMainBimetallicCompatibility: boolean;
  electricalDCMainBimetallicCompatibilityComment: string;
  electricalDCMainCableJointsCrimpsDies: string;

  // Other Section
  otherWaterIngressManholes: boolean;
  otherWaterIngressManholesComment: string;
  otherCableGlandsCondition: string;

  // Images
  operationalAspectsImages: OperationalAspectsImage[];
}

const initialData: OperationalAspectsData = {
  // Civil
  civilSiteConditions: "",
  civilSiteAccessRoadCondition: "",
  civilLVCablesManholesCondition: "",
  civilHVCablesManholesCondition: "",
  civilFoundationCondition: "",
  civilTransformerCondition: "",

  // Mechanical - Framework
  mechanicalFrameworkAppropriateRammingHead: false,
  mechanicalFrameworkAppropriateRammingHeadComment: "",
  mechanicalFrameworkAlignmentTwisted: false,
  mechanicalFrameworkAlignmentTwistedComment: "",
  mechanicalFrameworkRammingDepth: "",
  mechanicalFrameworkUndergroundSurprises: false,
  mechanicalFrameworkUndergroundSurprisesComment: "",
  mechanicalFrameworkRegalvanizationMethod: "",
  mechanicalFrameworkDrillingConcrete: false,
  mechanicalFrameworkDrillingConcreteComment: "",
  mechanicalFrameworkAverageRemainingTime: "",
  mechanicalFrameworkSignsOfCorrosion: false,
  mechanicalFrameworkSignsOfCorrosionComment: "",

  // Mechanical - Purlin
  mechanicalPurlinSignsOfCorrosion: false,
  mechanicalPurlinSignsOfCorrosionComment: "",
  mechanicalPurlinVisibleDeformation: false,
  mechanicalPurlinVisibleDeformationComment: "",

  // Mechanical - Module
  mechanicalModuleSynchronizedAlignment: false,
  mechanicalModuleSynchronizedAlignmentComment: "",
  mechanicalModuleClampInstallationCheck: false,
  mechanicalModuleClampInstallationCheckComment: "",

  // Electrical - String Above Ground
  electricalStringAboveLooseCableConnector: false,
  electricalStringAboveLooseCableConnectorComment: "",
  electricalStringAboveProperSupport: false,
  electricalStringAboveProperSupportComment: "",
  electricalStringAboveNoSharpEdge: false,
  electricalStringAboveNoSharpEdgeComment: "",
  electricalStringAboveCableNumbering: false,
  electricalStringAboveCableNumberingComment: "",

  // Electrical - String Below Ground
  electricalStringBelowRoutedThroughConduit: false,
  electricalStringBelowRoutedThroughConduitComment: "",
  electricalStringBelowConduitSealedWithFoam: false,
  electricalStringBelowConduitSealedWithFoamComment: "",

  // Electrical - DC Main
  electricalDCMainCablesWithoutEndCap: false,
  electricalDCMainCablesWithoutEndCapComment: "",
  electricalDCMainCableSpecification: "",
  electricalDCMainInstallationMethodology: "",
  electricalDCMainBimetallicCompatibility: false,
  electricalDCMainBimetallicCompatibilityComment: "",
  electricalDCMainCableJointsCrimpsDies: "",

  // Other
  otherWaterIngressManholes: false,
  otherWaterIngressManholesComment: "",
  otherCableGlandsCondition: "",

  // Images
  operationalAspectsImages: [],
};

export const OperationalAspectsForm = memo(function OperationalAspectsForm({
  siteId,
  templateId,
  reportId,
  sessionProgress,
  setSessionProgress,
  readOnly = false,
  previousCompletedReports = [],
}: OperationalAspectsFormProps) {
  const { notify } = useNotification();

  const { loading } = useAppSelector((state) => state?.files);
  const [formData, setFormData] = useState<OperationalAspectsData>(initialData);
  const formDataRef = useLatest(formData);
  const [selectedImage, setSelectedImage] =
    useState<OperationalAspectsImage | null>(null);
  const [imageToDelete, setImageToDelete] = useState<string | null>(null);
  const [uploadingImages, setUploadingImages] = useState<{
    [key: string]: boolean;
  }>({});

  const files = useVersionAwareFiles(readOnly);
  const [imageDescriptions, setImageDescriptions] = useImageDescriptions(files);

  const [expandedSections, setExpandedSections] = useState({
    civil: false,
    mechanical: false,
    electrical: false,
    other: false,
  });

  const dispatch = useAppDispatch();

  useEffect(() => {
    dispatch(
      fetchFiles({
        siteId,
        reportId,
      }),
    );
  }, [dispatch, siteId, reportId]);

  const currentSiteData = useVersionAwareSiteData(readOnly);
  const inverterType = (currentSiteData?.projectData?.inverterType || "")
    .toString()
    .trim()
    .toLowerCase();
  const isStringInverter = inverterType === "string";

  // Autosave unsaved edits to IndexedDB so nothing is lost if the app closes
  // before the debounced server save fires (e.g. offline, tab closed, phone
  // loses power).
  const draftKey =
    !readOnly && reportId ? `report:${reportId}:operational-aspects` : null;
  const { restoreDraft, clearSavedDraft } = useDraftAutosave(
    draftKey,
    formData,
  );
  const draftRestoredRef = useRef(false);

  // Restore any unsaved draft once on mount, before server data can
  // overwrite it below.
  useEffect(() => {
    if (!draftKey || draftRestoredRef.current) return;
    draftRestoredRef.current = true;
    restoreDraft().then((draft) => {
      if (draft?.data) {
        setFormData((prev) => ({ ...prev, ...draft.data }));
      }
    });
  }, [draftKey, restoreDraft]);

  // Once local form state matches the confirmed server record, the
  // IndexedDB draft is redundant — clear it so a future mount doesn't
  // restore stale local data over a fresher server record.
  useEffect(() => {
    if (!draftKey || !currentSiteData) return;
    if (JSON.stringify(currentSiteData) === JSON.stringify(formData)) {
      clearSavedDraft();
    }
  }, [draftKey, currentSiteData, formData, clearSavedDraft]);

  useEffect(() => {
    const prog = sharedOperationalAspectsProgress(formData,templateId);
    setSessionProgress((prev) => ({
      ...prev,
      [templateId]: { ...prev[templateId], "operational-aspects": prog },
    }));
  }, [formData, setSessionProgress, templateId]);

  const handleSave = useCallback(
    async (data: OperationalAspectsData) => {
      try {
        const mutationResult1 = await requireMutationSuccess(
          dispatch(
            updateSiteData({
              siteDataId: siteId,
              siteDataInfo: { operationalAspectsData: data },
            }),
          ),
        );
        const config = notificationHelpers.formSaveSuccess("Site Data", [
          mutationResult1,
        ]);
        notify(config.message, config.type, config.options);
      } catch (error) {
        const config = notificationHelpers.formSaveError(
          "Site Data",
          errorMessage(error),
        );
        notify(config.message, config.type, config.options);

        throw error;
      }
    },
    [siteId, dispatch, notify],
  );

  const {
    isSaving: isFormSaving,
    lastSaved: lastSavedForm,
    hasUnsavedChanges: hasFormUnsavedChanges,
    saveError: formSaveError,
    forceSave: forceFormSave,
    resetBaseline: resetFormBaseline,
  } = useDebouncedSave(formData, {
    delay: 7500,
    onSave: handleSave,
    onError: (error) => console.error("Save error:", error),
    readOnly,
  });

  useEffect(() => {
    if (currentSiteData) {
      const newFormData = {
        ...formDataRef.current,
        ...(currentSiteData?.operationalAspectsData || {}),
      };
      resetFormBaseline(newFormData);
      setFormData(newFormData);
    }
  }, [currentSiteData, resetFormBaseline, formDataRef]);

  const handleSaveImages = useCallback(
    async (descriptions: Record<string, string>) => {
      try {
        const mutationResult2 = await Promise.all(
          Object.entries(descriptions).map(([fileId, fileDescription]) =>
            requireMutationSuccess(
              dispatch(updateFile({ fileId, fileData: { fileDescription } })),
            ),
          ),
        );
        const config = notificationHelpers.formSaveSuccess(
          "Image Descriptions",
          [mutationResult2],
        );
        notify(config.message, config.type, config.options);
      } catch (error) {
        const config = notificationHelpers.formSaveError(
          "Image Descriptions",
          errorMessage(error),
        );
        notify(config.message, config.type, config.options);

        throw error;
      }
    },
    [dispatch, notify],
  );

  const {
    isSaving: isImageSaving,
    lastSaved: lastSavedImage,
    hasUnsavedChanges: hasImageUnsavedChanges,
    saveError: imageSaveError,
    forceSave: forceImageSave,
  } = useDebouncedSave(imageDescriptions, {
    delay: 5000,
    onSave: handleSaveImages,
    onError: (error) => console.error("Image description save error:", error),
    readOnly,
  });

  const isSaving = isFormSaving || isImageSaving;
  const hasUnsavedChanges = hasFormUnsavedChanges || hasImageUnsavedChanges;
  const saveError = formSaveError || imageSaveError;
  const lastSaved = useMemo(() => {
    if (!lastSavedForm) return lastSavedImage;
    if (!lastSavedImage) return lastSavedForm;
    return lastSavedForm > lastSavedImage ? lastSavedForm : lastSavedImage;
  }, [lastSavedForm, lastSavedImage]);

  const forceSave = useCallback(() => {
    forceFormSave();
    forceImageSave();
  }, [forceFormSave, forceImageSave]);

  const handleFieldChange = useCallback(
    (field: keyof OperationalAspectsData) => {
      return (value: string | boolean) => {
        setFormData((prev) => ({ ...prev, [field]: value }));
      };
    },
    [],
  );

  const toggleSection = useCallback(
    (section: keyof typeof expandedSections) => {
      setExpandedSections((prev) => ({
        ...prev,
        [section]: !prev[section],
      }));
    },
    [],
  );

  const handleImageUpload = useCallback(
    (files: FileList | File[] | null, filterData) => {
      if (!files) return;
      const fileArray = Array.from(files);

      const uploadKey = JSON.stringify(filterData);
      setUploadingImages((prev) => ({ ...prev, [uploadKey]: true }));

      requireMutationSuccess(
        dispatch(
          uploadMultipleFiles({
            files: fileArray,
            metadata: {
              siteId,
              entityType: `operational-aspects`,
              entityId: reportId,
              reportId,
            },
            filterData,
          }),
        ),
      )
        .then((mutationOutcome) => {
          fileArray?.forEach((file) => {
            const config = notificationHelpers.imageUploadSuccess(
              file.name,
              mutationOutcome,
            );
            notify(config.message, config.type, config.options);
          });
        })
        .catch((error) => {
          const config = notificationHelpers.imageUploadError(
            fileArray?.length === 1
              ? fileArray[0].name
              : `${fileArray?.length} files`,
            errorMessage(error),
          );
          notify(config.message, config.type, config.options);
        })
        .finally(() => {
          setUploadingImages((prev) => ({ ...prev, [uploadKey]: false }));
        });
    },
    [dispatch, siteId, reportId, notify],
  );

  const updateImageDescription = useCallback(
    (imageId: string, description: string) => {
      setImageDescriptions((prev) => ({ ...prev, [imageId]: description }));
    },
    [setImageDescriptions],
  );

  const handleRemoveImage = useCallback((imageId: string) => {
    setImageToDelete(imageId);
  }, []);

  const confirmRemoveImage = useCallback(() => {
    if (imageToDelete) {
      requireMutationSuccess(dispatch(deleteFile(imageToDelete)))
        .then((mutationOutcome) => {
          const config = notificationHelpers.imageDeleteSuccess(
            "Image",
            mutationOutcome,
          );
          notify(config.message, config.type, config.options);
        })
        .catch((error) => {
          const config = notificationHelpers.imageDeleteError(
            "Image",
            errorMessage(error),
          );
          notify(config.message, config.type, config.options);
        })
        .finally(() => {
          setImageToDelete(null);
        });
    }
  }, [imageToDelete, dispatch, notify]);

  const cancelRemoveImage = useCallback(() => {
    setImageToDelete(null);
  }, []);

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return (
      Number.parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i]
    );
  };

  const getLoadingStatus = () => {
    if (saveError) return "error";
    if (isSaving) return "saving";
    if (hasUnsavedChanges) return "unsaved";
    if (lastSaved) return "saved";
    return "idle";
  };

  const isUploadingForSection = (filterData: any) => {
    const uploadKey = JSON.stringify(filterData);
    return uploadingImages[uploadKey] || false;
  };

  // Helper component for checkbox with comment
  const CheckboxWithComment = useCallback(
    ({
      id,
      label,
      checked,
      onChange,
      commentValue,
      onCommentChange,
      description,
      readOnly,
    }: {
      id: string;
      label: string;
      checked: boolean;
      onChange: (value: boolean) => void;
      commentValue: string;
      onCommentChange: (value: string) => void;
      description?: string;
      readOnly?: boolean;
    }) => {
      return (
        <div className="space-y-2">
          <OptimizedCheckbox
            id={id}
            label={label}
            checked={checked}
            onChange={onChange}
            description={description}
            readOnly={readOnly}
          />
          <div className="ml-6 pl-4 border-l-2 border-gray-200 dark:border-border">
            <OptimizedTextarea
              id={`${id}-comment`}
              label="Comments"
              value={commentValue}
              onChange={onCommentChange}
              placeholder="Please provide additional details about this observation..."
              rows={2}
              readOnly={readOnly}
            />
          </div>
        </div>
      );
    },
    [],
  );

  // Image Card Component
  const ImageCard = useStableMemoComponent(
    function ImageCard({
      image,
      onView,
      onRemove,
      onDescriptionUpdate,
      isDeleting,
      imageDescription,
    }: {
      image: OperationalAspectsImage;
      onView: (image: OperationalAspectsImage) => void;
      onRemove: (id: string) => void;
      onDescriptionUpdate: (id: string, description: string) => void;
      isDeleting: boolean | string;
      imageDescription: string;
    }) {
      return (
        <div key={image.id} className="relative group">
          <div className="aspect-video bg-slate-100 rounded-lg overflow-hidden relative dark:bg-muted">
            <OfflineImage
              fileId={image?.id}
              src={image?.fileUrl}
              alt="Operational aspects image"
              className="w-full h-full object-cover"
            />
            {loading && (isDeleting === true || isDeleting === image.id) && (
              <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                <Loader2 className="w-6 h-6 text-white animate-spin" />
              </div>
            )}
          </div>
          <div className="absolute top-2 right-2 flex gap-1">
            <Button
              type="button"
              variant="secondary"
              size="sm"
              className="h-6 w-6 p-0 opacity-0 group-hover:opacity-100 transition-opacity bg-black/60 hover:bg-black/80 text-white rounded-full flex items-center justify-center"
              onClick={() => onView(image)}
              disabled={loading}
              title="View Image"
            >
              <Eye className="w-3 h-3" />
            </Button>
            {!readOnly && (
              <Button
                type="button"
                variant="destructive"
                size="sm"
                className="h-6 w-6 p-0 opacity-0 group-hover:opacity-100 transition-opacity bg-black/60 hover:bg-black/80 text-white rounded-full flex items-center justify-center"
                onClick={() => onRemove(image.id)}
                disabled={loading}
                title="Delete Image"
              >
                <Trash2 className="w-3 h-3" />
              </Button>
            )}
          </div>
          <div className="mt-2 space-y-1">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-slate truncate">
                {image?.originalName}
              </span>
              <Badge variant="outline" className="text-xs">
                {formatFileSize(image?.fileSize)}
              </Badge>
            </div>
            <OptimizedTextarea
              id={`desc-${image.id}`}
              label=""
              value={imageDescription}
              readOnly={readOnly}
              onChange={(value) => onDescriptionUpdate(image.id, value)}
              placeholder="Describe this operational aspect..."
              rows={2}
              disabled={loading}
            />
            <div className="text-xs text-slate-500 dark:text-muted-foreground">
              Date: {new Date(image.uploadDate).toLocaleDateString()}
            </div>
          </div>
        </div>
      );
    },
    (prevProps, nextProps) => {
      const propsEqual =
        prevProps.image.id === nextProps.image.id &&
        prevProps.imageDescription === nextProps.imageDescription &&
        prevProps.isDeleting === nextProps.isDeleting;
      return propsEqual;
    },
  );

  // Image Section Component
  const ImageSection = useStableMemoComponent(function ImageSection({
    title,
    filterData,
    uploadFilterData,
    emptyMessage,
    buttonText = "Add Images",
  }: {
    title: string;
    filterData: any;
    uploadFilterData: any;
    emptyMessage: string;
    buttonText?: string;
  }) {
    const sectionImages = files?.filter(
      (file) =>
        file?.relatedTo?.entityType === `operational-aspects` &&
        file?.relatedTo?.filterData &&
        JSON.stringify(file?.relatedTo?.filterData) ===
          JSON.stringify(filterData),
    );

    const isUploading = isUploadingForSection(uploadFilterData);

    return (
      <div className="space-y-4 pt-4 border-t">
        <div className="flex items-center justify-between">
          <h4 className="text-sm font-medium">{title}</h4>
          {!readOnly && (
            <ImageCapturePicker
              buttonText={buttonText}
              isUploading={isUploading}
              disabled={loading}
              onConfirm={(selectedFiles) =>
                handleImageUpload(selectedFiles, uploadFilterData)
              }
            />
          )}
        </div>

        {isUploading && (
          <div className="flex items-center justify-center py-6 border border-dashed border-slate-300 rounded-lg bg-slate-50 dark:bg-muted dark:border-border">
            <div className="text-center">
              <Loader2 className="w-6 h-6 text-slate-400 animate-spin mx-auto mb-2 dark:text-muted-foreground" />
              <p className="text-sm text-slate-600 dark:text-muted-foreground">Uploading images...</p>
            </div>
          </div>
        )}

        {!isUploading && sectionImages.length === 0 ? (
          <div className="flex items-center justify-between p-4 border border-dashed border-slate-300 rounded-lg bg-slate-50 dark:bg-muted dark:border-border">
            <div className="flex items-center gap-3">
              <Camera className="w-5 h-5 text-slate-400 dark:text-muted-foreground" />
              <div>
                <p className="text-sm font-medium text-slate-700 dark:text-foreground">
                  No images added
                </p>
                <p className="text-xs text-slate-500 dark:text-muted-foreground">{emptyMessage}</p>
              </div>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {sectionImages.map((image) => (
              <ImageCard
                key={image.id}
                imageDescription={
                  imageDescriptions[image.id] ?? image?.fileDescription ?? ""
                }
                image={image}
                onView={setSelectedImage}
                onRemove={handleRemoveImage}
                onDescriptionUpdate={updateImageDescription}
                isDeleting={loading ? imageToDelete : false}
              />
            ))}
          </div>
        )}
      </div>
    );
  });

  return (
    <div className="space-y-4 md:space-y-6 relative" >
      {!readOnly && (
        <LocalSectionImportControl
          previousCompletedReports={previousCompletedReports}
          sectionName="operational-aspects"
          siteId={siteId}
          reportId={reportId}
          readOnly={readOnly}
          setFormData={setFormData}
        />
      )}

      {!readOnly && (
        <div className="fixed top-25 right-3 z-40 sm:right-4">
          <div className="flex flex-row items-center gap-2 sm:flex-col sm:items-end sm:gap-3">
            {/* Status indicator - same size as save button */}
            {false && (
              <div
                className="
          bg-white/95 backdrop-blur-sm
          rounded-full border-2 border-gray-200/80
          shadow-lg
          w-12 h-12 sm:w-16 sm:h-16
          flex items-center justify-center
          group relative
          transition-all duration-300 ease-out
          transform hover:scale-110
          cursor-default
          p-2
        "
              >
                <div className="flex flex-col items-center justify-center w-full h-full">
                  <span className="text-[10px] font-medium text-gray-600 leading-none mb-2 dark:text-muted-foreground">
                    Saved
                  </span>

                  <div className="flex items-center justify-center h-6">
                    <LoadingIndicator
                      status={getLoadingStatus()}
                      lastSaved={lastSaved}
                      error={saveError}
                    />
                  </div>
                </div>

                {/* Tooltip - Fixed with pointer-events-none */}
                <div className="absolute right-full mr-2 px-2 py-1 bg-black text-white text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity duration-200 whitespace-nowrap pointer-events-none">
                  {getLoadingStatus() === "saving"
                    ? "Saving..."
                    : getLoadingStatus() === "saved"
                      ? `Saved ${lastSaved?.toLocaleTimeString()}`
                      : getLoadingStatus() === "error"
                        ? "Save failed"
                        : "Ready to save"}
                </div>

                {/* Error indicator */}
                {getLoadingStatus() === "error" && (
                  <div className="absolute -top-1 -right-1">
                    <div className="w-3 h-3 bg-red-500 rounded-full animate-pulse"></div>
                    <div className="w-3 h-3 bg-red-500 rounded-full absolute top-0 left-0 border-2 border-white"></div>
                  </div>
                )}
              </div>
            )}

            {/* Save button - Fixed with pointer-events-none */}
            <Button
              onClick={forceSave}
              disabled={isSaving || readOnly}
              className={`
          relative rounded-full w-12 h-12 sm:w-16 sm:h-16
          transition-all duration-300 ease-out
          flex flex-col items-center justify-center
          backdrop-blur-sm border-2
          shadow-lg hover:shadow-xl
          transform hover:scale-110 active:scale-95
          group
          ${
            hasUnsavedChanges && !readOnly
              ? "bg-gradient-to-br from-red-500 to-orange-500 hover:from-red-600 hover:to-orange-600 text-white border-red-300 dark:bg-gradient-to-br dark:border-red-300 dark:ring-red-300"
              : "bg-white/95 text-gray-600 border-gray-200/80 hover:bg-white/90 dark:bg-muted dark:text-muted-foreground dark:border-border dark:hover:bg-accent"
          }
          ${isSaving || readOnly ? "opacity-80 cursor-not-allowed" : "cursor-pointer"}
        `}
            >
              {isSaving ? (
                <div className="flex flex-col items-center justify-center">
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mb-2"></div>
                  <span className="text-[10px] leading-none">Saving</span>
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center">
                  <Save className="w-5 h-5 sm:mb-1" />
                  <span className="hidden text-[10px] font-medium leading-none sm:block">
                    Save
                  </span>
                </div>
              )}

              {/* Unsaved changes indicator */}
              {hasUnsavedChanges && !isSaving && (
                <div className="absolute -top-1 -right-1">
                  <div className="w-3 h-3 bg-red-500 rounded-full animate-ping"></div>
                  <div className="w-3 h-3 bg-red-500 rounded-full absolute top-0 left-0 border-2 border-white"></div>
                </div>
              )}

              {/* Tooltip - Fixed with pointer-events-none */}
              <div className="absolute right-full mr-2 px-2 py-1 bg-black text-white text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity duration-200 whitespace-nowrap pointer-events-none">
                {hasUnsavedChanges ? "Save changes" : "No changes to save"}
              </div>
            </Button>
          </div>
        </div>
      )}

      {/* Civil Section */}
      {currentSiteData?.installationType !== "rooftop" &&
        templateId !== "operational" && (
          <Card>
            <CardHeader
              className="cursor-pointer"
              onClick={() => toggleSection("civil")}
            >
              <div className="flex items-center justify-between">
                <CardTitle>Civil</CardTitle>
                <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                  {expandedSections.civil ? <ChevronUp /> : <ChevronDown />}
                </Button>
              </div>
            </CardHeader>
            {expandedSections.civil && (
              <CardContent className="space-y-4">
                <OptimizedTextarea
                  id="civilSiteAccessRoadCondition"
                  label="Describe the condition of the site access road"
                  value={formData.civilSiteAccessRoadCondition}
                  onChange={handleFieldChange("civilSiteAccessRoadCondition")}
                  placeholder="Describe condition of site access road..."
                  rows={3}
                  readOnly={readOnly}
                  required
                />

                <OptimizedTextarea
                  id="civilLVCablesManholesCondition"
                  label="Describe the condition of LV cables and manholes"
                  value={formData.civilLVCablesManholesCondition}
                  onChange={handleFieldChange("civilLVCablesManholesCondition")}
                  placeholder="Describe condition of LV cables and manholes..."
                  rows={3}
                  readOnly={readOnly}
                />

                <OptimizedTextarea
                  id="civilHVCablesManholesCondition"
                  label="Describe the condition of HV cables and manholes"
                  value={formData.civilHVCablesManholesCondition}
                  onChange={handleFieldChange("civilHVCablesManholesCondition")}
                  placeholder="Describe condition of HV cables and manholes..."
                  rows={3}
                  readOnly={readOnly}
                />

                {!isStringInverter ? (
                  <OptimizedTextarea
                    id="civilFoundationCondition"
                    label="Foundation condition of inverter stations and power electronics"
                    value={formData.civilFoundationCondition}
                    onChange={handleFieldChange("civilFoundationCondition")}
                    placeholder="Describe foundation conditions for inverter stations and power electronics..."
                    rows={3}
                    readOnly={readOnly}
                  />
                ) : (
                  <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-700">
                    Foundation checks for inverter stations and power
                    electronics are not applicable for string inverter sites.
                  </div>
                )}

                <OptimizedTextarea
                  id="civilTransformerCondition"
                  label="Transformer condition and any defects"
                  value={formData.civilTransformerCondition}
                  onChange={handleFieldChange("civilTransformerCondition")}
                  placeholder="Describe transformer condition, oil leaks, maintenance issues, or other observations..."
                  rows={3}
                  readOnly={readOnly}
                />

                <ImageSection
                  title="Civil Aspects Images"
                  filterData={{ civil_aspects: true }}
                  uploadFilterData={{ civil_aspects: true }}
                  emptyMessage="Add images related to civil aspects"
                  buttonText="Add Images"
                />
              </CardContent>
            )}
          </Card>
        )}

      {/* Mechanical Section */}
      <Card>
        <CardHeader
          className="cursor-pointer"
          onClick={() => toggleSection("mechanical")}
        >
          <div className="flex items-center justify-between">
            <CardTitle>Mechanical</CardTitle>
            <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
              {expandedSections.mechanical ? <ChevronUp /> : <ChevronDown />}
            </Button>
          </div>
        </CardHeader>
        {expandedSections.mechanical && (
          <CardContent className="space-y-6">
            {/* Framework Subsection */}
            <div className="space-y-4">
              <h4 className="text-sm font-medium text-slate-700 dark:text-foreground">
                Framework – Quality of Piling / Ramming
              </h4>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <CheckboxWithComment
                  id="mechanicalFrameworkAppropriateRammingHead"
                  label="Is the appropriate ramming head being used for the installation?"
                  checked={formData.mechanicalFrameworkAppropriateRammingHead}
                  onChange={handleFieldChange(
                    "mechanicalFrameworkAppropriateRammingHead",
                  )}
                  commentValue={
                    formData.mechanicalFrameworkAppropriateRammingHeadComment
                  }
                  onCommentChange={handleFieldChange(
                    "mechanicalFrameworkAppropriateRammingHeadComment",
                  )}
                  description="Check if the ramming head matches the specifications"
                  readOnly={readOnly}
                />

                <CheckboxWithComment
                  id="mechanicalFrameworkAlignmentTwisted"
                  label="Is there any visible alignment twisting or distortion?"
                  checked={formData.mechanicalFrameworkAlignmentTwisted}
                  onChange={handleFieldChange(
                    "mechanicalFrameworkAlignmentTwisted",
                  )}
                  commentValue={
                    formData.mechanicalFrameworkAlignmentTwistedComment
                  }
                  onCommentChange={handleFieldChange(
                    "mechanicalFrameworkAlignmentTwistedComment",
                  )}
                  description="Check for any misalignment or twisting issues"
                  readOnly={readOnly}
                />

                <CheckboxWithComment
                  id="mechanicalFrameworkSignsOfCorrosion"
                  label="Are there any visible signs of corrosion on the framework?"
                  checked={formData.mechanicalFrameworkSignsOfCorrosion}
                  onChange={handleFieldChange(
                    "mechanicalFrameworkSignsOfCorrosion",
                  )}
                  commentValue={
                    formData.mechanicalFrameworkSignsOfCorrosionComment
                  }
                  onCommentChange={handleFieldChange(
                    "mechanicalFrameworkSignsOfCorrosionComment",
                  )}
                  description="Check for rust, oxidation, or other corrosion indicators"
                  readOnly={readOnly}
                />
              </div>
            </div>

            {/* Purlin Subsection */}
            <div className="space-y-4 pt-4 border-t">
              <h4 className="text-sm font-medium text-slate-700 dark:text-foreground">
                Purlin & Other Attachments
              </h4>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <CheckboxWithComment
                  id="mechanicalPurlinSignsOfCorrosion"
                  label="Are there any signs of corrosion on the purlins?"
                  checked={formData.mechanicalPurlinSignsOfCorrosion}
                  onChange={handleFieldChange(
                    "mechanicalPurlinSignsOfCorrosion",
                  )}
                  commentValue={
                    formData.mechanicalPurlinSignsOfCorrosionComment
                  }
                  onCommentChange={handleFieldChange(
                    "mechanicalPurlinSignsOfCorrosionComment",
                  )}
                  description="Check for rust or corrosion on purlin surfaces"
                  readOnly={readOnly}
                />

                <CheckboxWithComment
                  id="mechanicalPurlinVisibleDeformation"
                  label="Is there any visible deformation on the purlins?"
                  checked={formData.mechanicalPurlinVisibleDeformation}
                  onChange={handleFieldChange(
                    "mechanicalPurlinVisibleDeformation",
                  )}
                  commentValue={
                    formData.mechanicalPurlinVisibleDeformationComment
                  }
                  onCommentChange={handleFieldChange(
                    "mechanicalPurlinVisibleDeformationComment",
                  )}
                  description="Check for bending, twisting, or other deformations"
                  readOnly={readOnly}
                />
              </div>
            </div>

            {/* Module Installation Subsection */}
            <div className="space-y-4 pt-4 border-t">
              <h4 className="text-sm font-medium text-slate-700 dark:text-foreground">
                Module Installation
              </h4>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <CheckboxWithComment
                  id="mechanicalModuleSynchronizedAlignment"
                  label="Are the modules properly aligned with synchronized alignment and symmetrical gaps?"
                  checked={formData.mechanicalModuleSynchronizedAlignment}
                  onChange={handleFieldChange(
                    "mechanicalModuleSynchronizedAlignment",
                  )}
                  commentValue={
                    formData.mechanicalModuleSynchronizedAlignmentComment
                  }
                  onCommentChange={handleFieldChange(
                    "mechanicalModuleSynchronizedAlignmentComment",
                  )}
                  description="Check alignment and gap consistency between modules"
                  readOnly={readOnly}
                />

                <CheckboxWithComment
                  id="mechanicalModuleClampInstallationCheck"
                  label="Are the module clamps properly installed and secured?"
                  checked={formData.mechanicalModuleClampInstallationCheck}
                  onChange={handleFieldChange(
                    "mechanicalModuleClampInstallationCheck",
                  )}
                  commentValue={
                    formData.mechanicalModuleClampInstallationCheckComment
                  }
                  onCommentChange={handleFieldChange(
                    "mechanicalModuleClampInstallationCheckComment",
                  )}
                  description="Verify correct clamp installation and torque"
                  readOnly={readOnly}
                />
              </div>
            </div>

            <ImageSection
              title="Mechanical Aspects Images"
              filterData={{ mechanical_aspects: true }}
              uploadFilterData={{ mechanical_aspects: true }}
              emptyMessage="Add images related to mechanical aspects"
              buttonText="Add Images"
            />
          </CardContent>
        )}
      </Card>

      {/* Electrical Section */}
      <Card>
        <CardHeader
          className="cursor-pointer"
          onClick={() => toggleSection("electrical")}
        >
          <div className="flex items-center justify-between">
            <CardTitle>Electrical</CardTitle>
            <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
              {expandedSections.electrical ? <ChevronUp /> : <ChevronDown />}
            </Button>
          </div>
        </CardHeader>
        {expandedSections.electrical && (
          <CardContent className="space-y-6">
            {/* String Cable Management Subsection */}
            <div className="space-y-4">
              <h4 className="text-sm font-medium text-slate-700 dark:text-foreground">
                String Cable Management (DC)
              </h4>
              <p className="text-sm text-slate-500 dark:text-muted-foreground">
                Tick each item if the condition is present on site.
              </p>

              <div className="space-y-4">
                <h5 className="text-sm font-medium text-slate-600 dark:text-muted-foreground">
                  Above Ground
                </h5>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <CheckboxWithComment
                    id="electricalStringAboveLooseCableConnector"
                    label="Are loose cables or connectors present on the ground?"
                    checked={formData.electricalStringAboveLooseCableConnector}
                    onChange={handleFieldChange(
                      "electricalStringAboveLooseCableConnector",
                    )}
                    commentValue={
                      formData.electricalStringAboveLooseCableConnectorComment
                    }
                    onCommentChange={handleFieldChange(
                      "electricalStringAboveLooseCableConnectorComment",
                    )}
                    description="Check for any loose or disconnected cables on the ground"
                    readOnly={readOnly}
                  />

                  <CheckboxWithComment
                    id="electricalStringAboveProperSupport"
                    label="Is there proper cable support near the MC4 connectors?"
                    checked={formData.electricalStringAboveProperSupport}
                    onChange={handleFieldChange(
                      "electricalStringAboveProperSupport",
                    )}
                    commentValue={
                      formData.electricalStringAboveProperSupportComment
                    }
                    onCommentChange={handleFieldChange(
                      "electricalStringAboveProperSupportComment",
                    )}
                    description="Check if cables are properly supported near connections"
                    readOnly={readOnly}
                  />

                  <CheckboxWithComment
                    id="electricalStringAboveNoSharpEdge"
                    label="Are there any sharp edges that could damage the cables?"
                    checked={formData.electricalStringAboveNoSharpEdge}
                    onChange={handleFieldChange(
                      "electricalStringAboveNoSharpEdge",
                    )}
                    commentValue={
                      formData.electricalStringAboveNoSharpEdgeComment
                    }
                    onCommentChange={handleFieldChange(
                      "electricalStringAboveNoSharpEdgeComment",
                    )}
                    description="Check for exposed sharp edges near cable routing"
                    readOnly={readOnly}
                  />

                  <CheckboxWithComment
                    id="electricalStringAboveCableNumbering"
                    label="Is the cable string numbering clearly visible and legible?"
                    checked={formData.electricalStringAboveCableNumbering}
                    onChange={handleFieldChange(
                      "electricalStringAboveCableNumbering",
                    )}
                    commentValue={
                      formData.electricalStringAboveCableNumberingComment
                    }
                    onCommentChange={handleFieldChange(
                      "electricalStringAboveCableNumberingComment",
                    )}
                    description="Check if string labels and markings are present and readable"
                    readOnly={readOnly}
                  />
                </div>
              </div>

              <div className="space-y-4 pt-4 border-t">
                <h5 className="text-sm font-medium text-slate-600 dark:text-muted-foreground">
                  Below Ground
                </h5>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <CheckboxWithComment
                    id="electricalStringBelowRoutedThroughConduit"
                    label="Is the cable properly routed through conduit?"
                    checked={formData.electricalStringBelowRoutedThroughConduit}
                    onChange={handleFieldChange(
                      "electricalStringBelowRoutedThroughConduit",
                    )}
                    commentValue={
                      formData.electricalStringBelowRoutedThroughConduitComment
                    }
                    onCommentChange={handleFieldChange(
                      "electricalStringBelowRoutedThroughConduitComment",
                    )}
                    description="Check if below-ground cables are routed through protective conduit"
                    readOnly={readOnly}
                  />

                  <CheckboxWithComment
                    id="electricalStringBelowConduitSealedWithFoam"
                    label="Is the conduit properly sealed with foam?"
                    checked={
                      formData.electricalStringBelowConduitSealedWithFoam
                    }
                    onChange={handleFieldChange(
                      "electricalStringBelowConduitSealedWithFoam",
                    )}
                    commentValue={
                      formData.electricalStringBelowConduitSealedWithFoamComment
                    }
                    onCommentChange={handleFieldChange(
                      "electricalStringBelowConduitSealedWithFoamComment",
                    )}
                    description="Check if conduit openings are properly sealed"
                    readOnly={readOnly}
                  />
                </div>
              </div>
            </div>

            {!isStringInverter && (
              <div className="space-y-4 pt-4 border-t">
                <h4 className="text-sm font-medium text-slate-700 dark:text-foreground">
                  DC Main Cable Management (Central Inverter)
                </h4>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <CheckboxWithComment
                    id="electricalDCMainCablesWithoutEndCap"
                    label="Are there any main cables installed without end caps?"
                    checked={formData.electricalDCMainCablesWithoutEndCap}
                    onChange={handleFieldChange(
                      "electricalDCMainCablesWithoutEndCap",
                    )}
                    commentValue={
                      formData.electricalDCMainCablesWithoutEndCapComment
                    }
                    onCommentChange={handleFieldChange(
                      "electricalDCMainCablesWithoutEndCapComment",
                    )}
                    description="Check for exposed cable ends without protective caps"
                    readOnly={readOnly}
                  />

                  <CheckboxWithComment
                    id="electricalDCMainBimetallicCompatibility"
                    label="Is there proper bi-metallic compatibility in the connections?"
                    checked={formData.electricalDCMainBimetallicCompatibility}
                    onChange={handleFieldChange(
                      "electricalDCMainBimetallicCompatibility",
                    )}
                    commentValue={
                      formData.electricalDCMainBimetallicCompatibilityComment
                    }
                    onCommentChange={handleFieldChange(
                      "electricalDCMainBimetallicCompatibilityComment",
                    )}
                    description="Check for appropriate bi-metallic connectors and compatibility"
                    readOnly={readOnly}
                  />
                </div>

                <OptimizedInput
                  id="electricalDCMainCableSpecification"
                  label="Cable specification"
                  value={formData.electricalDCMainCableSpecification}
                  onChange={handleFieldChange(
                    "electricalDCMainCableSpecification",
                  )}
                  placeholder="Enter cable specification"
                  readOnly={readOnly}
                />

                <OptimizedInput
                  id="electricalDCMainInstallationMethodology"
                  label="Installation methodology"
                  value={formData.electricalDCMainInstallationMethodology}
                  onChange={handleFieldChange(
                    "electricalDCMainInstallationMethodology",
                  )}
                  placeholder="Enter installation methodology"
                  readOnly={readOnly}
                />

                <OptimizedInput
                  id="electricalDCMainCableJointsCrimpsDies"
                  label="Cable joints / crimps / dies"
                  value={formData.electricalDCMainCableJointsCrimpsDies}
                  onChange={handleFieldChange(
                    "electricalDCMainCableJointsCrimpsDies",
                  )}
                  placeholder="Enter cable joints/crimps/dies details"
                  readOnly={readOnly}
                />
              </div>
            )}

            <ImageSection
              title="Electrical Aspects Images"
              filterData={{ electrical_aspects: true }}
              uploadFilterData={{ electrical_aspects: true }}
              emptyMessage="Add images related to electrical aspects"
              buttonText="Add Images"
            />
          </CardContent>
        )}
      </Card>

      {/* Other Section */}
      <Card>
        <CardHeader
          className="cursor-pointer"
          onClick={() => toggleSection("other")}
        >
          <div className="flex items-center justify-between">
            <CardTitle>Other</CardTitle>
            <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
              {expandedSections.other ? <ChevronUp /> : <ChevronDown />}
            </Button>
          </div>
        </CardHeader>
        {expandedSections.other && (
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 gap-4">
              <CheckboxWithComment
                id="otherWaterIngressManholes"
                label="Is there any water ingress observed in the manholes?"
                checked={formData.otherWaterIngressManholes}
                onChange={handleFieldChange("otherWaterIngressManholes")}
                commentValue={formData.otherWaterIngressManholesComment}
                onCommentChange={handleFieldChange(
                  "otherWaterIngressManholesComment",
                )}
                description="Check for water accumulation or leakage in electrical manholes"
                readOnly={readOnly}
              />
            </div>

            <OptimizedInput
              id="otherCableGlandsCondition"
              label="Cable glands condition"
              value={formData.otherCableGlandsCondition}
              onChange={handleFieldChange("otherCableGlandsCondition")}
              placeholder="Describe cable glands condition"
              readOnly={readOnly}
            />

            <ImageSection
              title="Other Operational Aspects Images"
              filterData={{ other_aspects: true }}
              uploadFilterData={{ other_aspects: true }}
              emptyMessage="Add images related to other operational aspects"
              buttonText="Add Images"
            />
          </CardContent>
        )}
      </Card>

      {/* Delete Confirmation Modal */}
      {imageToDelete && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-card rounded-lg max-w-md w-full p-6">
            <div className="flex items-center gap-3 mb-4">
              <AlertCircle className="w-6 h-6 text-red-500" />
              <h3 className="text-lg font-semibold">Delete Image</h3>
            </div>
            <p className="text-slate-600 dark:text-muted-foreground mb-6">
              Are you sure you want to delete this image? This action cannot be
              undone.
            </p>
            <div className="flex gap-3 justify-end">
              <Button
                variant="outline"
                onClick={cancelRemoveImage}
                disabled={loading}
              >
                Cancel
              </Button>
              <Button
                variant="destructive"
                onClick={confirmRemoveImage}
                disabled={loading}
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Deleting...
                  </>
                ) : (
                  <>
                    <Trash2 className="w-4 h-4 mr-2" />
                    Delete
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Image Preview Modal */}
      {selectedImage && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-card rounded-lg max-w-4xl max-h-full overflow-auto">
            <div className="p-4 border-b flex items-center justify-between">
              <h3 className="text-lg font-semibold">
                {selectedImage.originalName}
              </h3>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setSelectedImage(null)}
                disabled={loading}
              >
                <X className="w-4 h-4" />
              </Button>
            </div>
            <div className="p-4">
              <OfflineImage
                fileId={
                  selectedImage?._id ||
                  selectedImage?.id ||
                  selectedImage?._clientId
                }
                src={selectedImage?.fileUrl}
                alt={selectedImage.originalName}
                className="max-w-full max-h-96 mx-auto"
              />
              {selectedImage.fileDescription && (
                <div className="mt-4 p-3 bg-slate-50 dark:bg-muted rounded">
                  <p className="text-sm text-slate-700 dark:text-muted-foreground">
                    {selectedImage.fileDescription}
                  </p>
                </div>
              )}
              <div className="mt-2 text-sm text-slate-500 dark:text-muted-foreground">
                Size: {formatFileSize(selectedImage.fileSize)} • Date:{" "}
                {new Date(selectedImage.uploadDate).toLocaleDateString()}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Progress footer */}
      <div className="sticky bottom-2 md:bottom-4 z-20 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 p-3 md:p-4 bg-white dark:bg-card rounded-lg border shadow-lg backdrop-blur-sm bg-white/95 dark:bg-surface-raised/95">
        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-2 sm:gap-4 w-full sm:w-auto">
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-slate-700 dark:text-muted-foreground">
              Form Status:
            </span>
            <Badge variant="default">Complete</Badge>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-slate-700 dark:text-muted-foreground">
              Progress:
            </span>
            <Badge variant="outline">
              {sessionProgress?.[templateId]?.["operational-aspects"] ||
                sharedOperationalAspectsProgress(formData, templateId)}
              %
            </Badge>
          </div>
        </div>
        <div className="text-xs md:text-sm text-slate-500 dark:text-muted-foreground hidden lg:block">
          Auto-save enabled • Changes saved automatically
        </div>
      </div>
    </div>
  );
});
